import React, { useEffect, useRef, useState } from 'react';
import { API_CONFIG } from '../config/envConfig';
import { trackEvent } from '../utils/mixpanelClient';
import { EMAIL_REGEX, isPersonalEmail } from '../utils/emailValidation';

const SESSION_KEY = 'wo_callback_popup_v1';
const IDLE_MS = 5000;
const AUTO_CLOSE_MS = 4000;

function hasShownThisSession() {
  try {
    return sessionStorage.getItem(SESSION_KEY) === '1';
  } catch (_) {
    return false;
  }
}

function markShownThisSession() {
  try {
    sessionStorage.setItem(SESSION_KEY, '1');
  } catch (_) {
    // sessionStorage unavailable — the in-memory ref still prevents re-firing this tab.
  }
}

// Avoid stacking on top of the site's existing full-screen modals (sign in,
// sign up, pricing, etc.), which all share this z-index/backdrop pattern.
function hasBlockingModalOpen() {
  if (document.body.style.overflow === 'hidden') return true;
  if (document.querySelector('[aria-modal="true"], [role="dialog"]')) return true;
  if (document.querySelector('[class*="z-[9999]"], [class*="z-[99999]"]')) return true;
  return false;
}

const CallbackPopup = () => {
  const [stage, setStage] = useState('hidden'); // hidden | prompt | form | thanks-yes | thanks-no
  const [trigger, setTrigger] = useState(null);
  const [phone, setPhone] = useState('');
  const [email, setEmail] = useState('');
  const [formError, setFormError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const shownRef = useRef(hasShownThisSession());
  const idleTimerRef = useRef(null);

  const openPopup = (triggerType) => {
    if (shownRef.current) return;
    shownRef.current = true;
    markShownThisSession();
    setTrigger(triggerType);
    setStage('prompt');
    trackEvent('Callback Popup Shown', { trigger: triggerType });
  };

  useEffect(() => {
    if (shownRef.current) return undefined;

    const scheduleIdleCheck = () => {
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
      idleTimerRef.current = setTimeout(() => {
        if (shownRef.current) return;
        if (hasBlockingModalOpen()) {
          // User is busy elsewhere (e.g. sign-up modal) — wait for the next idle window instead of firing.
          scheduleIdleCheck();
          return;
        }
        openPopup('idle');
      }, IDLE_MS);
    };

    const handleActivity = () => {
      if (shownRef.current) return;
      scheduleIdleCheck();
    };

    const activityEvents = ['mousemove', 'scroll', 'keydown', 'touchstart', 'click'];
    activityEvents.forEach((evt) => window.addEventListener(evt, handleActivity, { passive: true }));
    scheduleIdleCheck();

    const handleMouseLeave = (e) => {
      if (shownRef.current) return;
      // Exit intent: cursor exits the top of the viewport, toward the tab/close/back controls.
      if (e.clientY <= 0 && !hasBlockingModalOpen()) {
        openPopup('exit_intent');
      }
    };
    document.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      activityEvents.forEach((evt) => window.removeEventListener(evt, handleActivity));
      document.removeEventListener('mouseleave', handleMouseLeave);
      if (idleTimerRef.current) clearTimeout(idleTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (stage !== 'thanks-yes' && stage !== 'thanks-no') return undefined;
    const timer = setTimeout(() => setStage('hidden'), AUTO_CLOSE_MS);
    return () => clearTimeout(timer);
  }, [stage]);

  useEffect(() => {
    if (stage === 'hidden') return undefined;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => {
      if (e.key === 'Escape') handleClose();
    };
    window.addEventListener('keydown', onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener('keydown', onKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stage]);

  const handleYes = () => {
    trackEvent('Callback Popup Yes Clicked', { trigger });
    setStage('form');
  };

  const handleNo = () => {
    trackEvent('Callback Popup No Clicked', { trigger });
    setStage('thanks-no');
  };

  const handleClose = () => {
    trackEvent('Callback Popup Closed', { trigger, stage });
    setStage('hidden');
  };

  const handleChange = (setter) => (e) => {
    setter(e.target.value);
    setFormError('');
  };

  const validate = () => {
    const trimmedEmail = email.trim();
    if (!trimmedEmail) {
      setFormError('Please enter your work email.');
      return false;
    }
    if (!EMAIL_REGEX.test(trimmedEmail)) {
      setFormError('Please enter a valid work email address');
      return false;
    }
    if (isPersonalEmail(trimmedEmail)) {
      setFormError('Please enter a valid work email address');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    const payload = {
      email: email.trim(),
      phone: phone.trim(),
    };

    try {
      const response = await fetch(API_CONFIG.callbackRequestUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        throw new Error(`Callback request failed with status ${response.status}`);
      }

      trackEvent('Callback Requested', {
        trigger,
        has_phone: Boolean(payload.phone),
        has_email: Boolean(payload.email),
      });
      setStage('thanks-yes');
    } catch (_) {
      setFormError('Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (stage === 'hidden') return null;

  return (
    <div
      className="fixed inset-0 z-[9999] bg-black bg-opacity-50 flex items-center justify-center p-4"
      onClick={handleClose}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Request a callback"
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-sm rounded-2xl bg-white border border-gray-200 shadow-2xl overflow-hidden"
      >
        <div className="flex items-start justify-end px-5 pt-4">
          <button
            type="button"
            aria-label="Close"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600 text-lg leading-none"
          >
            &times;
          </button>
        </div>

        <div className="px-5 pb-5 pt-2">
          {stage === 'prompt' && (
            <>
              <p className="text-sm font-medium text-gray-900 mb-1">Want to see it in action?</p>
              <p className="text-sm text-gray-600 mb-4">We&apos;d be happy to walk you through it.</p>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={handleYes}
                  className="flex-1 px-4 py-2 rounded-full text-sm font-medium text-white bg-[#171717] hover:bg-black"
                >
                  Yes
                </button>
                <button
                  type="button"
                  onClick={handleNo}
                  className="flex-1 px-4 py-2 rounded-full text-sm font-medium border border-gray-300 text-gray-800 hover:bg-gray-50"
                >
                  Not now
                </button>
              </div>
            </>
          )}

          {stage === 'form' && (
            <form onSubmit={handleSubmit} className="space-y-3">
              <p className="text-sm font-medium text-gray-900">Let&apos;s arrange a quick walkthrough</p>
              <p className="text-sm text-gray-600">
                Share your details, and we&apos;ll get in touch.
              </p>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Phone number (Optional)</label>
                <input
                  type="tel"
                  value={phone}
                  onChange={handleChange(setPhone)}
                  placeholder="+1 555 000 0000"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  disabled={submitting}
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Work email*</label>
                <input
                  type="email"
                  value={email}
                  onChange={handleChange(setEmail)}
                  placeholder="you@company.com"
                  className="w-full px-3 py-2 rounded-lg border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                  disabled={submitting}
                  required
                />
              </div>
              {formError && <p className="text-xs text-red-600">{formError}</p>}
              <button
                type="submit"
                disabled={submitting}
                className={`w-full px-4 py-2 rounded-full text-sm font-medium text-white bg-[#171717] hover:bg-black ${
                  submitting ? 'opacity-60 cursor-not-allowed' : ''
                }`}
              >
                {submitting ? 'Submitting...' : 'Get in touch'}
              </button>
              <p className="text-xs text-gray-500 text-center">No pressure. Just a quick conversation.</p>
            </form>
          )}

          {stage === 'thanks-yes' && (
            <div className="text-center py-2">
              <p className="text-sm font-medium text-gray-900">Thanks! We&apos;ll be in touch soon.</p>
              <p className="text-xs text-gray-500 mt-1">Our team will reach out shortly.</p>
            </div>
          )}

          {stage === 'thanks-no' && (
            <div className="text-center py-2">
              <p className="text-sm font-medium text-gray-900">No problem, thanks for visiting!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CallbackPopup;
